/**
 * Public detail paths whose document head can be resolved after a client navigation.
 * Rejects off-site, protocol-relative, and traversal values before any lookup.
 */

const DETAIL_PREFIX =
  /^\/(agencies|venues|events|blog|courses)\/([A-Za-z0-9][A-Za-z0-9._-]{0,180})$/;

const RESERVED_SLUGS: Record<string, ReadonlySet<string>> = {
  agencies: new Set(["org-chart"]),
  courses: new Set(["compare", "requirements", "topic", "domains"]),
};

export function normalizePublicSeoPath(pathname: string): string {
  const bare = pathname.split("?")[0]?.split("#")[0] ?? "";
  const trimmed = bare.replace(/\/+$/, "") || "/";
  return trimmed === "/index.html" ? "/" : trimmed;
}

/** Same-origin agency, venue, event, blog, or course detail path, or null. */
export function parsePublicSeoDetailPath(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed || trimmed.length > 300) return null;
  if (
    trimmed.includes("://") ||
    trimmed.startsWith("//") ||
    trimmed.includes("\\") ||
    trimmed.includes("..")
  ) {
    return null;
  }
  let path = trimmed;
  if (path.includes("%")) {
    try {
      path = decodeURIComponent(path);
    } catch {
      return null;
    }
  }
  if (
    !path.startsWith("/") ||
    path.startsWith("//") ||
    path.includes("://") ||
    path.includes("\\") ||
    path.includes("..") ||
    path.includes("?") ||
    path.includes("#") ||
    path.includes("@")
  ) {
    return null;
  }
  const normalized = normalizePublicSeoPath(path);
  const match = DETAIL_PREFIX.exec(normalized);
  if (!match) return null;
  const prefix = match[1] ?? "";
  const slug = (match[2] ?? "").toLowerCase();
  if (RESERVED_SLUGS[prefix]?.has(slug)) return null;
  return normalized;
}
